/**
 * KANON 03's two lists, projected: the requests waiting on a decision, and the relationships that
 * have ended.
 *
 * Both endpoints are keyset-paged, so what arrives is an array of pages rather than an array of
 * rows, and both carry a `hasMore` the caller needs in order to know whether "load more" means
 * anything. Flattening those pages happens here, once, and whether it may drop a repeat is decided
 * per list rather than as a house rule: the requests inbox keys on the invitation id and skips a
 * repeat, the ARCHIVE keys on nothing and skips nothing. Each function's own note says why, and the
 * archive's is the correction of an earlier guard that was silently eating real rows.
 *
 * Pure: no React, no DOM, no query hook, no i18next. Dates leave as `Date`s and month counts leave
 * as numbers; `Intl` does the wording at the cell, in the trainer's locale.
 *
 * The two endpoints used to spell the same person differently — the archive sent `fullName` /
 * `initials`, the requests inbox sent `clientFullName` / `clientInitials`. As of the 25 August 2026
 * contract both send one nested person, so the name and the two letters are read, never composed,
 * and this module holds no initials rule of its own.
 */

import type {
  TrainWithMeModulesClientsDtosResponsesArchivedClientPageResponse as ArchivedPage,
  TrainWithMeModulesClientsDtosResponsesCoachingRequestPageResponse as RequestPage,
} from '~/shared/api/generated/model';

/** One ended coaching relationship, as the archive row draws it. */
export interface ArchivedRelationship {
  readonly customerId: number;
  readonly fullName: string;
  readonly initials: string;
  readonly startedAt: Date;
  readonly endedAt: Date;
  /** Whole months the relationship lasted. `0` for anything under one month. */
  readonly months: number;
}

/** One person asking to be coached, as the request row draws it. */
export interface CoachingRequest {
  readonly invitationId: number;
  readonly customerId: number;
  readonly fullName: string;
  readonly initials: string;
  readonly requestedAt: Date;
  /**
   * When the offer lapses on its own, or `null` where the wire did not send one.
   *
   * Carried because the ZGŁOSZENIE column has to say how long a request has been waiting AND
   * whether it is running out — and "running out" is only answerable against the deadline the
   * server set. Nothing here computes the lifetime; the backend owns it.
   */
  readonly expiresAt: Date | null;
}

/** A flattened keyset list, plus whether the server says there is more behind it. */
export interface InboxList<T> {
  readonly rows: readonly T[];
  readonly hasMore: boolean;
}

function parsed(value: string | null | undefined): Date | null {
  if (value == null) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function trimmedOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Whole months between two instants, by calendar rather than by division.
 *
 * KANON draws "marzec 2025 – styczeń 2026 · 10 mies.", which is the calendar difference: dividing
 * elapsed milliseconds by an average month gives 10.4 here and 11 for a relationship that ran one
 * day into the twelfth month. The day-of-month check is what stops "1 March → 31 March" reporting
 * a month that has not finished.
 */
function wholeMonthsBetween(from: Date, to: Date): number {
  const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  const beforeAnniversary = to.getDate() < from.getDate();
  return Math.max(0, beforeAnniversary ? months - 1 : months);
}

/**
 * The archive.
 *
 * A row without a customer id, a name or both timestamps is dropped rather than rendered blank:
 * every action the row offers ("Zaproś ponownie") needs the id, and the line under the name is the
 * date range.
 *
 * ── There is NO de-duplication here, and that is the correction ──────────────────────────────
 * An earlier revision kept a `Set` of customer ids and skipped a repeat, justified as "a keyset
 * cursor that lands on a tie can repeat a row". Both halves of that were wrong:
 *
 * · **The cursor cannot repeat a row.** `CoachingRelationshipRepository.GetArchivedByTrainerAsync`
 *   pages on `EndedAt < cursor OR (EndedAt == cursor AND Id < cursorId)` — strictly less than, on
 *   the same pair it orders by — so the row the cursor names is excluded from the next page by
 *   construction.
 * · **A customer id is not a row identity here.** This endpoint pages RELATIONSHIPS, and
 *   `IX_CoachingRelationships_CustomerId_Open` is filtered on `EndedAt IS NULL`, so exactly one
 *   OPEN engagement is unique per client and any number of ended ones may coexist. End → invite
 *   again → end is an ordinary trainer loop, and the second ended engagement was silently
 *   swallowed by the guard that was supposed to be protecting it.
 *
 * The wire carries no relationship id (`GetArchivedClientResponse` is identity plus the two
 * timestamps), so the row's identity for React is the pair `customerId` + `endedAt`, which is the
 * same pair the backend's own cursor is total on. Raised as a backend follow-up.
 */
export function archivedRelationships(
  pages: readonly ArchivedPage[] | undefined,
): InboxList<ArchivedRelationship> {
  const rows: ArchivedRelationship[] = [];

  for (const page of pages ?? []) {
    for (const dto of page.clients ?? []) {
      const customerId = dto.customerId;
      const startedAt = parsed(dto.startedAt);
      const endedAt = parsed(dto.endedAt);
      const fullName = trimmedOrNull(dto.person?.fullName);
      if (typeof customerId !== 'number' || startedAt === null || endedAt === null) continue;
      if (fullName === null) continue;

      rows.push({
        customerId,
        fullName,
        initials: trimmedOrNull(dto.person?.initials) ?? '',
        startedAt,
        endedAt,
        months: wholeMonthsBetween(startedAt, endedAt),
      });
    }
  }

  return { rows, hasMore: pages?.[pages.length - 1]?.hasMore === true };
}

/**
 * The requests inbox. Dropped for the same reasons, keyed on the invitation rather than the person.
 *
 * The `Set` here is NOT the archive's mistake repeated: an invitation id is this row's whole
 * identity, so a skip can only ever drop a genuine repeat of the same invitation, never a second
 * real request. It is a belt on a cursor that is already strict.
 */
export function coachingRequests(
  pages: readonly RequestPage[] | undefined,
): InboxList<CoachingRequest> {
  const rows: CoachingRequest[] = [];
  const seen = new Set<number>();

  for (const page of pages ?? []) {
    for (const dto of page.requests ?? []) {
      const invitationId = dto.invitationId;
      const customerId = dto.customerId;
      const requestedAt = parsed(dto.createdAt);
      const fullName = trimmedOrNull(dto.client?.fullName);
      if (typeof invitationId !== 'number' || typeof customerId !== 'number') continue;
      if (requestedAt === null || fullName === null) continue;
      if (seen.has(invitationId)) continue;
      seen.add(invitationId);

      rows.push({
        invitationId,
        customerId,
        fullName,
        initials: trimmedOrNull(dto.client?.initials) ?? '',
        requestedAt,
        expiresAt: parsed(dto.expiresAt),
      });
    }
  }

  return { rows, hasMore: pages?.[pages.length - 1]?.hasMore === true };
}
